import { Alert, Autocomplete, Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { MAX_CATEGORY_HIERARCHY_LEVEL } from "@/constants/products.constants";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCategories from "@/hooks/requests/category/use-get-categories";
import ICategory from "@/interfaces/product.category.interfaces";
import { ProductCategoryFormMessages } from "@/messages/product-categories/product-categories-form.messages";

import { CategoryFormData } from "./types";

type Props = {
  messages: ProductCategoryFormMessages;
  isLoading: boolean;
  defaultParentCode?: string | null;
};

const PAGINATION_CONFIG = {
  pageNumber: 1,
  pageSize: 1000,
} as const;

const GRID_SIZES = {
  FULL_WIDTH: { xs: 12, lg: 12 },
  HALF_WIDTH: { xs: 12, sm: 6 },
} as const;

const NO_CATEGORIES_MESSAGE = "No categories available";

function CategoryForm({ messages, isLoading, defaultParentCode }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);
  const formik = useFormikContext<CategoryFormData>();
  const { values, handleChange, submitForm, errors, setFieldValue } = formik;

  const { data } = useGetCategories(PAGINATION_CONFIG);
  const categories = useMemo(() => data?.items || [], [data]);

  const availableParentCategories = useMemo(() => {
    return categories.filter((category) => category.level < MAX_CATEGORY_HIERARCHY_LEVEL);
  }, [categories]);

  useEffect(() => {
    if (defaultParentCode && !values.isEditing) {
      const category = categories.find((c) => c.code === defaultParentCode);
      if (category) {
        setSelectedCategory(category);
        setFieldValue("parentCode", defaultParentCode);
        setFieldValue("level", category.level + 1);
      }
    } else {
      const category = categories.find((c) => c.code === values.parentCode);
      if (category) {
        setSelectedCategory(category);
        setFieldValue("level", category.level + 1);
      }
    }
  }, [categories, values, defaultParentCode, setFieldValue]);

  const handleSelectParentCategory = (newCategory: ICategory | null) => {
    setSelectedCategory(newCategory);
    setFieldValue("parentCode", newCategory?.code || "");
    if (newCategory) {
      setFieldValue("level", newCategory.level + 1);
    } else {
      setFieldValue("level", 1);
    }
  };

  const showLevelWarning =
    selectedCategory && selectedCategory.level === MAX_CATEGORY_HIERARCHY_LEVEL - 1;

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid size={GRID_SIZES.FULL_WIDTH}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between">
                <MDTypography variant="h5" fontWeight={"bold"}>
                  {values.isEditing ? messages.editVariationTitle : messages.newVariationTitle}
                </MDTypography>
              </MDBox>

              <MDBox mt={1}>
                <Grid container spacing={3}>
                  <Grid size={GRID_SIZES.HALF_WIDTH}>
                    <FormField
                      variant="standard"
                      name="description"
                      label={messages.descriptionLabel}
                      value={values.description}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.description}
                    />
                  </Grid>{" "}
                  <Grid size={GRID_SIZES.HALF_WIDTH}>
                    <Autocomplete
                      fullWidth
                      options={availableParentCategories}
                      getOptionLabel={(option) => option.description || ""}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      value={selectedCategory || null}
                      onChange={(_, newValue) => {
                        handleSelectParentCategory(newValue || null);
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.code || option.id}>
                          {option.description}
                        </li>
                      )}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label={messages.parentCodeLabel}
                          variant="standard"
                          error={!!errors.parentCode}
                          helperText={errors.parentCode}
                        />
                      )}
                      noOptionsText={NO_CATEGORIES_MESSAGE}
                    />
                  </Grid>
                </Grid>

                {selectedCategory && showLevelWarning && (
                  <MDBox mt={2}>
                    <Alert severity="warning">
                      <strong>Atenção:</strong> Esta categoria será criada no nível{" "}
                      {MAX_CATEGORY_HIERARCHY_LEVEL} (último nível permitido). Não será possível
                      criar subcategorias dentro desta categoria.
                    </Alert>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <MDBox mt={2}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={12}>
            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="gradient"
                color="light"
                onClick={() => router(ROUTE_URLS.productCategories(lang))}
                isLoading={isLoading}
              >
                {messages.cancelButton}
              </MDButton>

              <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
                {messages.updateButton}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default CategoryForm;
